import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Pause, Volume2, Sparkles } from 'lucide-react';

interface StoryPart {
  id: number;
  english: string;
  french: string;
  image: string;
  vocabulary: string[];
  colors: string[];
  numbers?: number[];
  animals: string[];
}

const storyParts: StoryPart[] = [
  {
    id: 1,
    english: "Meet Betty! She is a happy girl with brown hair. Today, Betty finds a magical door in her garden. The door is blue and sparkles like stars!",
    french: "Voici Betty ! C'est une fille heureuse avec des cheveux bruns. Aujourd'hui, Betty trouve une porte magique dans son jardin. La porte est bleue et brille comme des étoiles !",
    image: "/images/betty_magical_character_20251125_192517.png",
    vocabulary: ["happy", "girl", "brown", "hair", "magical", "door", "garden", "blue", "sparkles", "stars"],
    colors: ["blue", "brown"],
    animals: [],
    numbers: [1]
  },
  {
    id: 2,
    english: "Betty opens the magical door and sees a beautiful garden! There are red roses, yellow sunflowers, and purple butterflies. She counts five butterflies dancing in the air!",
    french: "Betty ouvre la porte magique et voit un beau jardin ! Il y a des roses rouges, des tournesols jaunes et des papillons violets. Elle compte cinq papillons qui dansent dans l'air !",
    image: "/images/fantasy_creatures_1.jpeg",
    vocabulary: ["opens", "beautiful", "garden", "red", "roses", "yellow", "sunflowers", "purple", "butterflies", "dancing"],
    colors: ["red", "yellow", "purple"],
    animals: ["butterflies"],
    numbers: [5]
  },
  {
    id: 3,
    english: "In the garden, Betty meets Luna the unicorn! Luna is white with a pink horn. She has three baby unicorns: one is silver, one is gold, and one is rainbow colored!",
    french: "Dans le jardin, Betty rencontre Luna la licorne ! Luna est blanche avec une corne rose. Elle a trois bébés licornes : une est argentée, une est dorée, et une est arc-en-ciel !",
    image: "/images/fantasy_creatures_4.jpeg",
    vocabulary: ["meets", "unicorn", "white", "pink", "horn", "baby", "silver", "gold", "rainbow", "colored"],
    colors: ["white", "pink", "silver", "gold", "rainbow"],
    animals: ["unicorn", "baby unicorns"],
    numbers: [3, 1]
  },
  {
    id: 4,
    english: "Betty and Luna find Spark the friendly dragon! Spark is green with orange wings. He is sad because he lost his four magical gems: red, blue, yellow, and green gems!",
    french: "Betty et Luna trouvent Spark le dragon amical ! Spark est vert avec des ailes oranges. Il est triste parce qu'il a perdu ses quatre gemmes magiques : des gemmes rouges, bleues, jaunes et vertes !",
    image: "/images/fantasy_creatures_2.jpeg",
    vocabulary: ["find", "friendly", "dragon", "green", "orange", "wings", "sad", "lost", "gems", "magical"],
    colors: ["green", "orange", "red", "blue", "yellow"],
    animals: ["dragon"],
    numbers: [4]
  },
  {
    id: 5,
    english: "Betty, Luna, and Spark work together! They find all four gems and become best friends. The magical garden is happy again with two unicorns, one dragon, and one special girl!",
    french: "Betty, Luna et Spark travaillent ensemble ! Ils trouvent les quatre gemmes et deviennent les meilleurs amis. Le jardin magique est à nouveau heureux avec deux licornes, un dragon et une fille spéciale !",
    image: "/images/magical_friendship_scene_20251125_192538.png",
    vocabulary: ["work", "together", "find", "best", "friends", "happy", "again", "special"],
    colors: [],
    animals: ["unicorns", "dragon"],
    numbers: [4, 2, 1]
  }
];

export const StorySection: React.FC = () => {
  const [currentPart, setCurrentPart] = useState(0);
  const [showFrench, setShowFrench] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const playAudio = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = showFrench ? 'fr-FR' : 'en-US';
      utterance.rate = 0.7; // Slower for young learners
      utterance.pitch = 1.2; // Higher pitch for children
      
      // Try to select a female voice for Betty
      const voices = speechSynthesis.getVoices();
      const femaleVoice = voices.find(voice => 
        voice.lang.startsWith(showFrench ? 'fr' : 'en') && 
        (voice.name.toLowerCase().includes('female') || 
         voice.name.toLowerCase().includes('woman') ||
         voice.name.toLowerCase().includes('samantha') ||
         voice.name.toLowerCase().includes('karen') ||
         voice.name.toLowerCase().includes('susan'))
      );
      
      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }
      
      utterance.onstart = () => setIsPlaying(true);
      utterance.onend = () => setIsPlaying(false);
      
      speechSynthesis.speak(utterance);
    }
  };

  const stopAudio = () => {
    speechSynthesis.cancel();
    setIsPlaying(false);
  };

  const currentStory = storyParts[currentPart];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="mb-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-purple-600 flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6" />
            Betty's Magical Friendship Adventure
            <Sparkles className="w-6 h-6" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <img 
                src={currentStory.image} 
                alt={`Betty's adventure part ${currentPart + 1}`}
                className="w-full h-64 object-cover rounded-lg shadow-lg border-4 border-purple-200"
              />
              
              <div className="flex gap-2 justify-center flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFrench(!showFrench)}
                  className="bg-purple-50 hover:bg-purple-100 border-purple-300"
                >
                  {showFrench ? '🇬🇧 English' : '🇫🇷 Français'}
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => isPlaying ? stopAudio() : playAudio(showFrench ? currentStory.french : currentStory.english)}
                  className="bg-pink-50 hover:bg-pink-100 border-pink-300"
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  <Volume2 className="w-4 h-4 ml-1" />
                  Listen to Betty
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border-2 border-purple-200">
                <p className="text-lg leading-relaxed font-medium">
                  {showFrench ? currentStory.french : currentStory.english}
                </p>
              </div>

              {/* Vocabulary Section */}
              <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200">
                <h4 className="font-semibold text-orange-700 mb-2 flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  Magic Words:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {currentStory.vocabulary.map((word, index) => (
                    <span 
                      key={index} 
                      className="px-2 py-1 bg-yellow-100 text-orange-800 rounded-full text-sm cursor-pointer hover:bg-yellow-200 border border-yellow-300"
                      onClick={() => playAudio(word)}
                    >
                      {word}
                    </span>
                  ))}
                </div>
              </div>

              {/* Colors Section */}
              {currentStory.colors.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                  <h4 className="font-semibold text-blue-700 mb-2">🌈 Colors in the Story:</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentStory.colors.map((color, index) => (
                      <span 
                        key={index} 
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm cursor-pointer hover:bg-blue-200 border border-blue-300"
                        onClick={() => playAudio(color)}
                      >
                        {color}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Animals Section */}
              {currentStory.animals.length > 0 && (
                <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                  <h4 className="font-semibold text-green-700 mb-2">🦄 Magical Friends:</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentStory.animals.map((animal, index) => (
                      <span 
                        key={index} 
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm cursor-pointer hover:bg-green-200 border border-green-300"
                        onClick={() => playAudio(animal)}
                      >
                        {animal}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Numbers Section */}
              {currentStory.numbers && currentStory.numbers.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
                  <h4 className="font-semibold text-red-700 mb-2">🔢 Numbers in the Story:</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentStory.numbers.map((number, index) => (
                      <span 
                        key={index} 
                        className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm cursor-pointer hover:bg-red-200 border border-red-300 font-bold"
                        onClick={() => playAudio(number.toString())}
                      >
                        {number}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-between">
                <Button
                  onClick={() => setCurrentPart(Math.max(0, currentPart - 1))}
                  disabled={currentPart === 0}
                  variant="outline"
                  className="bg-purple-50 hover:bg-purple-100 border-purple-300"
                >
                  ← Previous
                </Button>
                
                <span className="flex items-center text-sm text-purple-600 font-medium">
                  Part {currentPart + 1} of {storyParts.length}
                </span>
                
                <Button
                  onClick={() => setCurrentPart(Math.min(storyParts.length - 1, currentPart + 1))}
                  disabled={currentPart === storyParts.length - 1}
                  variant="outline"
                  className="bg-purple-50 hover:bg-purple-100 border-purple-300"
                >
                  Next →
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};